import { useState } from "react";
import { useMutation } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PROJECT_TYPES = [
  { value: "website", label: "Website", icon: "🌐", description: "Marketing or informational site" },
  { value: "web-app", label: "Web App", icon: "💻", description: "Interactive web application" },
  { value: "saas", label: "SaaS", icon: "☁️", description: "Software as a Service product" },
  { value: "mobile-app", label: "Mobile App", icon: "📱", description: "iOS or Android application" },
  { value: "games", label: "Games", icon: "🎮", description: "Video game or interactive experience" },
  { value: "brand-identity", label: "Brand Identity", icon: "✨", description: "Logo, colors, and brand assets" },
  { value: "feature-module", label: "Feature Module", icon: "🔧", description: "Specific feature or component" },
  { value: "design-system", label: "Design System", icon: "🎨", description: "Complete component library" },
  { value: "other", label: "Other", icon: "📦", description: "Custom project type" },
] as const;

const BRAND_PERSONALITY_OPTIONS = [
  "Professional", "Playful", "Minimal", "Bold", "Elegant",
  "Modern", "Classic", "Friendly", "Luxurious", "Energetic",
  "Calm", "Technical", "Creative", "Trustworthy", "Innovative",
];

export function CreateProjectModal({ isOpen, onClose }: CreateProjectModalProps) {
  const navigate = useNavigate();
  const createProject = useMutation(api.projects.createProject);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    type: "website" as typeof PROJECT_TYPES[number]["value"],
    targetAudience: "",
    brandPersonality: [] as string[],
    goals: [] as string[],
  });
  const [goalInput, setGoalInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Please enter a project name");
      return;
    }

    setIsSubmitting(true);
    try {
      const projectId = await createProject({
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        type: formData.type,
        targetAudience: formData.targetAudience.trim() || undefined,
        brandPersonality: formData.brandPersonality.length > 0 ? formData.brandPersonality : undefined,
        goals: formData.goals.length > 0 ? formData.goals : undefined,
      });

      toast.success("Project created successfully!");
      onClose();
      navigate(`/projects/${projectId}`);
    } catch (error) {
      console.error("Error creating project:", error);
      toast.error(error instanceof Error ? error.message : "Failed to create project");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddGoal = () => {
    if (goalInput.trim() && !formData.goals.includes(goalInput.trim())) {
      setFormData({
        ...formData,
        goals: [...formData.goals, goalInput.trim()],
      });
      setGoalInput("");
    }
  };

  const handleRemoveGoal = (goal: string) => {
    setFormData({
      ...formData,
      goals: formData.goals.filter((g) => g !== goal),
    });
  };

  const togglePersonality = (personality: string) => {
    if (formData.brandPersonality.includes(personality)) {
      setFormData({
        ...formData,
        brandPersonality: formData.brandPersonality.filter((p) => p !== personality),
      });
    } else {
      setFormData({
        ...formData,
        brandPersonality: [...formData.brandPersonality, personality],
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-2xl w-full max-h-[90vh] bg-base-100 border-2 border-base-300 overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-base-content uppercase tracking-tight">Create New Project</h3>
            <button
              type="button"
              onClick={onClose}
              className="btn btn-sm btn-circle btn-ghost"
              aria-label="Close modal"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-6">

            {/* Project Name */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">Project Name *</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input input-bordered w-full"
                placeholder="My Awesome Project"
                required
              />
            </div>

            {/* Description */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">Description</span>
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="textarea textarea-bordered w-full"
                placeholder="Brief description of your project..."
              />
            </div>

            {/* Project Type */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">Project Type</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                {PROJECT_TYPES.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, type: type.value })}
                    className={`
                      p-4 rounded-lg border-2 text-left transition-all
                      ${
                        formData.type === type.value
                          ? "border-gray-800 bg-info-light"
                          : "border-base-300 hover:border-gray-800"
                      }
                    `}
                  >
                    <div className="flex items-start">
                      <span className="text-2xl mr-3">{type.icon}</span>
                      <div className="flex-1">
                        <div className="text-sm font-medium">{type.label}</div>
                        <div className="text-xs opacity-60 mt-1">{type.description}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Target Audience */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">Target Audience</span>
              </label>
              <input
                type="text"
                value={formData.targetAudience}
                onChange={(e) => setFormData({ ...formData, targetAudience: e.target.value })}
                className="input input-bordered w-full"
                placeholder="e.g., Young professionals, Enterprise users"
              />
            </div>

            {/* Brand Personality */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">Brand Personality (select up to 5)</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {BRAND_PERSONALITY_OPTIONS.map((personality) => (
                  <button
                    key={personality}
                    type="button"
                    onClick={() => togglePersonality(personality)}
                    disabled={
                      !formData.brandPersonality.includes(personality) &&
                      formData.brandPersonality.length >= 5
                    }
                    className={`
                      badge badge-lg transition-colors cursor-pointer
                      ${
                        formData.brandPersonality.includes(personality)
                          ? "bg-info-light border-gray-800 text-gray-900"
                          : "badge-outline hover:badge-neutral"
                      }
                      disabled:opacity-50 disabled:cursor-not-allowed
                    `}
                  >
                    {personality}
                  </button>
                ))}
              </div>
            </div>

            {/* Goals */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">Project Goals</span>
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={goalInput}
                  onChange={(e) => setGoalInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddGoal();
                    }
                  }}
                  className="input input-bordered flex-1"
                  placeholder="Add a goal and press Enter"
                />
                <button
                  type="button"
                  onClick={handleAddGoal}
                  className="btn btn-outline"
                >
                  Add
                </button>
              </div>
              {formData.goals.length > 0 && (
                <div className="space-y-2 mt-2">
                  {formData.goals.map((goal) => (
                    <div
                      key={goal}
                      className="flex items-center justify-between p-2 bg-base-200 rounded-md"
                    >
                      <span className="text-sm">{goal}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveGoal(goal)}
                        className="btn btn-ghost btn-xs btn-circle"
                        aria-label="Remove goal"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="modal-action">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="btn btn-ghost"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !formData.name.trim()}
              className="btn px-4 py-2 bg-info-light text-black font-bold uppercase hover:bg-[#B3D9F5] border-info-light hover:border-[#B3D9F5]"
            >
              {isSubmitting ? "Creating..." : "Create Project"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
